import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DatePreset, DateRangeValue } from "../types";
import {
  Popup,
  Presets,
  Calendar,
  SectionTitle,
  PresetButton,
  CalendarHeader,
  MonthYear,
  NavButton,
  WeekdayRow,
  WeekdayLabel,
  DateGrid,
  DateButton,
} from "../styles";

// ─── types ────────────────────────────────────────────────────────────────

type SelectorProps = {
  presets?: DatePreset[];
  onChange: (value: DateRangeValue) => void;
  highlightCol?: string;
  menuPlacement?: "left" | "right";
  /** Pre-selected range to restore highlights when popup reopens */
  initialValue?: DateRangeValue;
};

type DateCell = {
  date: Date;
  isCurrentMonth: boolean;
  isDisabled: boolean;
};

// ─── pure date helpers ────────────────────────────────────────────────────

const startOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

const endOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isAfter = (a: Date, b: Date) => a.getTime() > b.getTime();
const isBefore = (a: Date, b: Date) => a.getTime() < b.getTime();
const isSameOrAfter = (a: Date, b: Date) => a.getTime() >= b.getTime();
const isSameOrBefore = (a: Date, b: Date) => a.getTime() <= b.getTime();

const formatMonthYear = (d: Date): string =>
  new Intl.DateTimeFormat("en", { month: "long", year: "numeric" }).format(d);

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// ─── calendar grid builder ────────────────────────────────────────────────

const buildCalendar = (year: number, month: number): DateCell[] => {
  const today = startOfDay(new Date());
  const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells: DateCell[] = [];

  // Padding — previous month
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    cells.push({
      date: new Date(year, month - 1, daysInPrevMonth - i),
      isCurrentMonth: false,
      isDisabled: true, // other-month days are never selectable
    });
  }

  // Current month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    cells.push({
      date,
      isCurrentMonth: true,
      isDisabled: isAfter(startOfDay(date), today), // no future dates
    });
  }

  // Padding — next month (fill up to 42 cells = 6 rows)
  const remaining = 42 - cells.length;
  for (let day = 1; day <= remaining; day++) {
    cells.push({
      date: new Date(year, month + 1, day),
      isCurrentMonth: false,
      isDisabled: true,
    });
  }

  return cells;
};

// ─── component ────────────────────────────────────────────────────────────

type SelectionState =
  | { phase: "idle" }
  | { phase: "selecting"; start: Date };

const Selector: React.FC<SelectorProps> = ({
  presets,
  onChange,
  highlightCol = "#1d7f7a",
  menuPlacement = "left",
  initialValue,
}) => {
  const { t } = useTranslation();
  const now = new Date();

  // Navigate to the month of the selected start date if available
  const initDate = initialValue?.[0] ?? now;
  const [viewMonth, setViewMonth] = useState(initDate.getMonth());
  const [viewYear, setViewYear] = useState(initDate.getFullYear());

  const [selection, setSelection] = useState<SelectionState>({ phase: "idle" });

  // Restore previously selected range so highlights show on reopen
  const [range, setRange] = useState<DateRangeValue>(initialValue ?? [null, null]);

  const presetList = useMemo(() => presets ?? [], [presets]);
  const todayStart = useMemo(() => startOfDay(new Date()), []);

  const cells = useMemo(
    () => buildCalendar(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  // ── navigation ──────────────────────────────────────────────────────────

  const canGoNext = useMemo(() => {
    const n = new Date();
    return !(viewMonth === n.getMonth() && viewYear === n.getFullYear());
  }, [viewMonth, viewYear]);

  const prevMonth = useCallback(() => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  }, [viewMonth]);

  const nextMonth = useCallback(() => {
    if (!canGoNext) return;
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  }, [canGoNext, viewMonth]);

  // ── date click ──────────────────────────────────────────────────────────

  const handleDateClick = useCallback(
    (cell: DateCell) => {
      if (cell.isDisabled || !cell.isCurrentMonth) return;

      const clicked = startOfDay(cell.date);

      if (selection.phase === "idle") {
        // First click → anchor start
        setSelection({ phase: "selecting", start: clicked });
        setRange([clicked, null]);
      } else {
        // Second click → complete range (auto-swap if needed)
        const { start } = selection;
        const [finalStart, finalEnd] = isBefore(clicked, start)
          ? [clicked, endOfDay(start)]
          : [start, endOfDay(clicked)];

        const confirmed: DateRangeValue = [finalStart, finalEnd];
        setRange(confirmed);
        setSelection({ phase: "idle" });
        onChange(confirmed);
      }
    },
    [selection, onChange]
  );

  // ── preset click ────────────────────────────────────────────────────────

  const handlePreset = useCallback(
    ({ start, end }: DatePreset) => {
      const confirmed: DateRangeValue = [startOfDay(start), endOfDay(end)];
      setRange(confirmed);
      setSelection({ phase: "idle" });
      onChange(confirmed);
    },
    [onChange]
  );

  // ── render helpers ──────────────────────────────────────────────────────

  const [rangeStart, rangeEnd] = range;

  const isSelected = (date: Date) =>
    (rangeStart != null && isSameDay(date, rangeStart)) ||
    (rangeEnd != null && isSameDay(date, rangeEnd));

  const isInRange = (date: Date) =>
    rangeStart != null &&
    rangeEnd != null &&
    isSameOrAfter(date, rangeStart) &&
    isSameOrBefore(date, rangeEnd);

  return (
    <Popup menuPlacement={menuPlacement}>
      {presetList.length > 0 && (
        <Presets>
          <SectionTitle>Presets</SectionTitle>
          {presetList.map((p, i) => (
            <PresetButton
              key={`${p.title}-${i}`}
              type="button"
              onClick={() => handlePreset(p)}
            >
              {p.title}
            </PresetButton>
          ))}
        </Presets>
      )}

      <Calendar>
        {/* Header */}
        <CalendarHeader>
          <NavButton type="button" onClick={prevMonth} aria-label="Previous month">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path fillRule="evenodd" d="M15.78 4.22a.75.75 0 010 1.06L9.06 12l6.72 6.72a.75.75 0 11-1.06 1.06l-7.25-7.25a.75.75 0 010-1.06l7.25-7.25a.75.75 0 011.06 0z" clipRule="evenodd" />
            </svg>
          </NavButton>

          <MonthYear>{formatMonthYear(new Date(viewYear, viewMonth))}</MonthYear>

          <NavButton
            type="button"
            onClick={nextMonth}
            disabled={!canGoNext}
            className={!canGoNext ? "disabled" : ""}
            aria-label="Next month"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path fillRule="evenodd" d="M8.22 19.78a.75.75 0 010-1.06L14.94 12 8.22 5.28a.75.75 0 011.06-1.06l7.25 7.25a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0z" clipRule="evenodd" />
            </svg>
          </NavButton>
        </CalendarHeader>

        {/* Weekday labels */}
        <WeekdayRow>
          {WEEKDAYS.map((d) => (
            <WeekdayLabel key={d}>{d}</WeekdayLabel>
          ))}
        </WeekdayRow>

        {/* Date grid */}
        <DateGrid>
          {cells.map((cell, idx) => {
            const sel = isSelected(cell.date);
            const inRng = !sel && isInRange(cell.date);
            const today = isSameDay(cell.date, todayStart);

            return (
              <DateButton
                key={`${viewYear}-${viewMonth}-${idx}`}
                type="button"
                onClick={() => handleDateClick(cell)}
                isSelected={sel}
                isInRange={inRng}
                isDisabled={cell.isDisabled}
                isOtherMonth={!cell.isCurrentMonth}
                isToday={today}
                highlightCol={highlightCol}
                aria-label={cell.date.toDateString()}
                aria-pressed={sel}
                aria-disabled={cell.isDisabled || !cell.isCurrentMonth}
              >
                {cell.date.getDate()}
              </DateButton>
            );
          })}
        </DateGrid>

        {/* Selection hint */}
        {selection.phase === "selecting" && (
          <SectionTitle style={{ marginTop: 10, marginBottom: 0, color: highlightCol }}>
            {t('common.select_end_date')}
          </SectionTitle>
        )}
      </Calendar>
    </Popup>
  );
};

export default memo(Selector);
